<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="permission-modal"
      dialog-width="30rem"
    >
      <template #header>
        {{ ls({ ja: '権限の管理', en: 'Permission Management' }) }}
      </template>
      <div class="jps-modal-content">
        <table class="jps-table">
          <thead>
            <tr>
              <th>機関</th>
              <th class="lv">権限</th>
              <th class="del">削除</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="org in orgs">
              <td>
                <UiOrgSpan :id="org.id" />
              </td>
              <td>
                <div class="jps-select">
                  <select
                    v-model="org.permLv"
                    :disabled="
                      userStore.getOrgId === org.id ||
                      _data.permission.ownerOrg === org.id
                    "
                  >
                    <option v-for="lv in levels" :value="lv" :key="lv">
                      {{ led(permissionLevelDef, lv) }}
                    </option>
                  </select>
                </div>
              </td>
              <td>
                <button
                  class="jps-button"
                  @click="confrimRemove(orgs, org)"
                  :disabled="
                    userStore.getOrgId === org.id ||
                    _data.permission.ownerOrg === org.id
                  "
                >
                  <BaseIcon size="size-6" name="mdi/delete" />
                </button>
              </td>
            </tr>
            <tr>
              <td colspan="3">
                <button class="jps-button" @click="addOrg()">
                  機関を追加する
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="ok()" :disabled="!permissionUpdated">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
    </UiModalDialog>
    <PermissionOrganizationModal ref="permissionOrganizationModal" />
  </BaseModal>
</template>

<script lang="ts">
export interface OrganizationWithPermission {
  id: string
  permLv: PermissionLevel
}
</script>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal, PermissionOrganizationModal } from '#components'

const { ls, led } = useI18n()
const userStore = useUserStore()

interface Props {
  data?: { permission: Permission }
}
const props = withDefaults(defineProps<Props>(), {
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _data: Ref<{ permission: Permission }> = ref()
const orgs: Ref<OrganizationWithPermission[]> = ref([])
const orgsInitial: Ref<string> = ref('')
const openAwait = async (options?: Props) => {
  setOptionsValue(_data, 'data', props, options)

  let _orgs: OrganizationWithPermission[] = []
  if (_data.value.permission.adminOrg)
    _data.value.permission.adminOrg.forEach((o) => {
      _orgs.push({ id: o, permLv: 'ADMIN' })
    })
  if (_data.value.permission.editOrg)
    _data.value.permission.editOrg.forEach((o) => {
      _orgs.push({ id: o, permLv: 'EDIT' })
    })
  if (_data.value.permission.readOrg)
    _data.value.permission.readOrg.forEach((o) => {
      _orgs.push({ id: o, permLv: 'READ' })
    })
  orgs.value = _orgs
  orgsInitial.value = JSON.stringify(orgs.value)

  return await modal.value.openAwait<Permission>()
}
defineExpose({ openAwait })

const permissionLevelDef = PERMISSION_LEVEL_DEF
const levels: PermissionLevel[] = ['ADMIN', 'EDIT', 'READ']

const permissionOrganizationModal = ref<InstanceType<typeof PermissionOrganizationModal>>()
const addOrg = async () => {
  try {
    const org = await permissionOrganizationModal.value.openAwait()
    if (!org) return
    orgs.value.push({ id: org.id, permLv: 'EDIT' })
  } catch (cancel) {}
}

const confrimRemove = (list, value) => {
  removeFromArray(list, value)
}

const ok = () => {
  let p: Permission = {}
  p.adminOrg = orgs.value
    .filter((org) => org.permLv === 'ADMIN')
    .map((org) => org.id)
  p.editOrg = orgs.value
    .filter((org) => org.permLv === 'EDIT')
    .map((org) => org.id)
  p.readOrg = orgs.value
    .filter((org) => org.permLv === 'READ')
    .map((org) => org.id)
  p.publicationLevel = _data.value.permission.publicationLevel
  p.ownerOrg = _data.value.permission.ownerOrg
  p.level = _data.value.permission.level
  modal.value.resolve(p)
}

const permissionUpdated = computed(() => {
  return orgsInitial.value !== JSON.stringify(orgs.value)
})
</script>

<style lang="scss" scoped>
.permission-modal {

  td {
    vertical-align: middle;
  }
  .lv {
    width: 8rem;
  }
  .del {
    width: 4rem;
  }
}
</style>
